import { extractLeafNodeLabels } from '../components/InstrumentMenu';

/**
 * Extract all valid instrument names from the hierarchical InstrumentMenuOptions
 * Only includes leaf-level (selectable) instruments, not parent categories
 * @returns {string[]} Array of all valid instrument names
 */
export const extractValidInstrumentNames = () => {
  const validNames = [];
  
  // Get only leaf node labels (selectable instruments)
  const leafLabels = extractLeafNodeLabels();
  
  for (const label of leafLabels) {
    // // Extract the English name (before parentheses)
    // const englishName = label.split('(')[0].trim();
    // validNames.push(englishName);
    
    // Also add the full label for exact matching
    validNames.push(label);
  }
  
  // Add the special allowed values
  validNames.push('Unknown');
  validNames.push('Delete');
  
  return validNames;
};

/**
 * Check if a track name is valid according to NeuroMuse instrument list
 * @param {string} trackName - The track name to validate
 * @returns {boolean} True if valid, false if invalid
 */
export const isValidInstrumentName = (trackName) => {
  if (!trackName || typeof trackName !== 'string') {
    return false;
  }
  
  const normalizedTrackName = trackName.trim();
  
  // First check special cases (case-insensitive)
  const lowerTrackName = normalizedTrackName.toLowerCase();
  if (lowerTrackName === 'unknown' || lowerTrackName === 'delete') {
    return true;
  }
  
  // Then check NeuroMuse instruments (case-sensitive, exact match only)
  const validNames = extractValidInstrumentNames();
  
  // Remove the special cases from validNames for case-sensitive checking
  const instrumentNames = validNames.filter(name => 
    name.toLowerCase() !== 'unknown' && name.toLowerCase() !== 'delete'
  );
  
  // Check for exact case-sensitive match
  for (const validName of instrumentNames) {
    if (normalizedTrackName === validName) {
      return true;
    }
  }
  
  return false;
};

/**
 * Validate all instrument tracks in an annotation sheet
 * @param {Object} annotationSheet - The annotation sheet object
 * @returns {Object} Validation result with isValid boolean and invalidTracks array
 */
export const validateInstrumentTracks = (annotationSheet) => {
  const result = {
    isValid: true,
    invalidTracks: []
  };
  
  if (!annotationSheet || !annotationSheet.instrument_tracks) {
    return result;
  }
  
  for (const track of annotationSheet.instrument_tracks) {
    const trackName = track.name || track.track_id || '';
    
    if (!isValidInstrumentName(trackName)) {
      result.isValid = false;
      result.invalidTracks.push({
        trackId: track.track_id,
        trackName: trackName,
        instrument: track.instrument || ''
      });
    }
  }
  
  return result;
};

/**
 * Error messages for instrument validation
 */
export const INSTRUMENT_VALIDATION_ERROR_MESSAGES = {
  TITLE: 'Invalid Instrument Detected',
  ENGLISH: 'The file contains instruments not from the NeuroMuse instrument list and thus may be a corrupted file. Please immediately report this error to the admin team to look into!',
  CHINESE: '该文件包含不在 NeuroMuse 乐器列表中的乐器，因此可能是一个损坏的文件。请立即将此错误报告给管理员团队进行调查!',
  RETURN_BUTTON: 'Return (返回)'
};
